import { createClient, type SupabaseClient } from "@supabase/supabase-js";
import { getConfig } from "../config";

export interface SessionInfo {
  accessToken: string;
  email: string;
}

export interface AuthGateway {
  getSession(): Promise<SessionInfo | null>;
  signIn(email: string, password: string): Promise<SessionInfo>;
  signUp(email: string, password: string): Promise<SessionInfo>;
  signOut(): Promise<void>;
  refreshSession(): Promise<SessionInfo>;
  resetPassword(email: string, redirectTo: string): Promise<void>;
  updatePassword(password: string): Promise<void>;
  onAuthStateChange(cb: (session: SessionInfo | null) => void): () => void;
  getAccessToken(): string | null;
}

function mapSession(session: Awaited<ReturnType<SupabaseClient["auth"]["getSession"]>>["data"]["session"]): SessionInfo | null {
  if (!session) return null;
  return { accessToken: session.access_token, email: session.user.email ?? "" };
}

export function createSupabaseAuthGateway(): AuthGateway {
  const config = getConfig();
  const client = createClient(config.supabaseUrl, config.supabaseAnonKey);
  let lastToken: string | null = null;

  const track = (session: SessionInfo | null): SessionInfo | null => {
    lastToken = session?.accessToken ?? null;
    return session;
  };

  return {
    async getSession() {
      const { data } = await client.auth.getSession();
      return track(mapSession(data.session));
    },

    async signIn(email, password) {
      const { data, error } = await client.auth.signInWithPassword({ email, password });
      if (error) throw error;
      return track(mapSession(data.session)) ?? { accessToken: "", email };
    },

    async signUp(email, password) {
      const { data, error } = await client.auth.signUp({ email, password });
      if (error) throw error;
      // Com confirmação de e-mail desabilitada o signup retorna sessão; senão,
      // tenta a sessão existente e falha com mensagem clara pedindo confirmação.
      const session = data.session ?? (await client.auth.getSession()).data.session;
      if (!session) throw new Error("Confirme seu e-mail para ativar a conta antes de entrar.");
      return track(mapSession(session)) ?? { accessToken: "", email };
    },

    async signOut() {
      await client.auth.signOut();
      lastToken = null;
    },

    async refreshSession() {
      const { data, error } = await client.auth.refreshSession();
      if (error) throw error;
      return track(mapSession(data.session)) ?? { accessToken: "", email: "" };
    },

    async resetPassword(email, redirectTo) {
      const { error } = await client.auth.resetPasswordForEmail(email, { redirectTo });
      if (error) throw error;
    },

    async updatePassword(password) {
      const { error } = await client.auth.updateUser({ password });
      if (error) throw error;
    },

    onAuthStateChange(cb) {
      const { data } = client.auth.onAuthStateChange((_event, session) => {
        cb(track(mapSession(session)));
      });
      return () => data.subscription.unsubscribe();
    },

    getAccessToken() {
      return lastToken;
    },
  };
}
